/**
 * Per-service closed actual hint and "Bill actual" action on the Payments card (#2706).
 * Open timers are ignored. Planned/manual latch is not written here.
 */

import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { closedActualHours, formatDecimalHoursLabel } from '@/utils/actualTime';

export interface BillActualServiceRow {
  bookingServiceId: string;
  name: string;
  laborItems: readonly unknown[];
  actualDurationHours?: number | null;
  /** Fixed-price services have no hourly billable quantity (#2706). */
  pricingType?: string | null;
}

interface BillActualServicesProps {
  services: readonly BillActualServiceRow[];
  canEditBillableHours: boolean;
  billingLocked: boolean;
  pendingServiceId: string | null;
  onRequestBillActual: (bookingServiceId: string) => void;
}

export function BillActualServices({
  services,
  canEditBillableHours,
  billingLocked,
  pendingServiceId,
  onRequestBillActual,
}: BillActualServicesProps) {
  const { t } = useTranslation('workorders');
  const rows = services
    .map((service) => ({
      service,
      closedHours: closedActualHours(service),
    }))
    .filter(({ service, closedHours }) => service.laborItems.length > 0 && closedHours > 0);

  if (rows.length === 0) return null;

  return (
    <Stack spacing={1} data-testid="bill-actual-services" sx={{ mt: 1.5 }}>
      {rows.map(({ service, closedHours }) => {
        const hoursLabel = formatDecimalHoursLabel(closedHours);
        const pending = pendingServiceId === service.bookingServiceId;
        const canBillActual = canEditBillableHours && service.pricingType !== 'fixed';
        return (
          <Box
            key={service.bookingServiceId}
            data-testid={`bill-actual-row-${service.bookingServiceId}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`bill-actual-hint-${service.bookingServiceId}`}
            >
              {t('detail.actualHoursHint', { name: service.name, hours: hoursLabel })}
            </Typography>
            {canBillActual && (
              <Button
                size="small"
                variant="outlined"
                data-testid={`bill-actual-${service.bookingServiceId}`}
                aria-label={t('detail.billActualAria', { name: service.name })}
                disabled={billingLocked || pending}
                onClick={() => onRequestBillActual(service.bookingServiceId)}
              >
                {t('detail.billActual', { hours: hoursLabel })}
              </Button>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
